/**
 * Helper utilities for React Query context bridge
 */

import { REACT_QUERY_CONTEXT_CONFIG } from './config';

import type { AIContextConfig } from './types';
import type { ContextType } from '../../context/types';
import type { QueryKey } from '@tanstack/react-query';

/**
 * Resource name mappings for scope and type inference.
 * Centralizes the mapping logic to avoid duplication.
 */
const RESOURCE_MAPPINGS: Record<string, { scope: string; type: ContextType }> = {
  sales: { scope: 'sales', type: 'sales-data' },
  financial: { scope: 'financials', type: 'financial-data' },
  financials: { scope: 'financials', type: 'financial-data' },
  product: { scope: 'products', type: 'product-data' },
  products: { scope: 'products', type: 'product-data' },
  customer: { scope: 'customers', type: 'customer-data' },
  customers: { scope: 'customers', type: 'customer-data' },
  inventory: { scope: 'inventory', type: 'inventory-data' },
  pricing: { scope: 'pricing', type: 'pricing-data' },
  discount: { scope: 'discounts', type: 'marketing-data' },
  discounts: { scope: 'discounts', type: 'marketing-data' },
  loyalty: { scope: 'loyalty', type: 'customer-data' },
  brand: { scope: 'brands', type: 'product-data' },
  brands: { scope: 'brands', type: 'product-data' },
  category: { scope: 'categories', type: 'product-data' },
  categories: { scope: 'categories', type: 'product-data' },
  strain: { scope: 'strains', type: 'product-data' },
  strains: { scope: 'strains', type: 'product-data' },
  vendor: { scope: 'vendors', type: 'product-data' },
  vendors: { scope: 'vendors', type: 'product-data' },
  tag: { scope: 'tags', type: 'product-data' },
  tags: { scope: 'tags', type: 'product-data' },
  segment: { scope: 'segments', type: 'customer-data' },
  segments: { scope: 'segments', type: 'customer-data' },
  report: { scope: 'reports', type: 'financial-data' },
  reports: { scope: 'reports', type: 'financial-data' },
};

/**
 * Get the first key from a React Query key as a string.
 *
 * @param queryKey - React Query key array
 * @returns The first key as a string, or empty string if invalid
 */
export function getFirstQueryKey(queryKey: QueryKey): string {
  if (!Array.isArray(queryKey) || queryKey.length === 0) {
    return '';
  }
  return String(queryKey[0]);
}

/**
 * Parse a query key into its component parts.
 *
 * Query key format: 'resource-action' (e.g., 'product-detail', 'sales-summary')
 *
 * @param queryKey - React Query key
 * @returns Object with baseName and optional action
 */
export function parseQueryKeyParts(queryKey: QueryKey): { action?: string; baseName: string } {
  const firstKey = getFirstQueryKey(queryKey);
  if (!firstKey) {
    return { baseName: '' };
  }

  const parts = firstKey.split('-');
  return {
    baseName: parts[0], // e.g., 'sales', 'product', 'customer'
    action: parts[1], // e.g., 'summary', 'detail', 'list'
  };
}

/**
 * Infer scope from base resource name.
 *
 * @param baseName - Base resource name (e.g., 'product', 'sales')
 * @returns Scope string or undefined if not mapped
 */
export function inferScope(baseName: string): string | undefined {
  return RESOURCE_MAPPINGS[baseName.toLowerCase()]?.scope;
}

/**
 * Infer context type from resource name.
 *
 * @param baseName - Base resource name (e.g., 'product', 'sales')
 * @returns Context type
 */
export function inferType(baseName: string): ContextType {
  return RESOURCE_MAPPINGS[baseName.toLowerCase()]?.type || 'other';
}

/**
 * Extract AI context configuration from query metadata.
 *
 * @param meta - Query metadata (may contain aiContext config)
 * @returns AI context config, boolean, or undefined
 */
export function getAIContextConfig(meta: unknown): AIContextConfig | boolean | undefined {
  if (!meta || typeof meta !== 'object') {
    return undefined;
  }
  return (meta as { aiContext?: AIContextConfig | boolean }).aiContext;
}

/**
 * Get maximum context size from metadata or use default.
 *
 * @param meta - Query metadata (may contain aiContext config)
 * @returns Maximum size in bytes
 */
export function getMaxContextSize(meta: unknown): number {
  const aiContextConfig = getAIContextConfig(meta);
  if (typeof aiContextConfig === 'object' && aiContextConfig.maxSize) {
    return aiContextConfig.maxSize;
  }
  return REACT_QUERY_CONTEXT_CONFIG.MAX_CONTEXT_SIZE;
}

/**
 * Stringify data and calculate its size.
 *
 * @param data - Data to stringify
 * @returns Object with JSON string and size in bytes
 */
export function stringifyAndSize(data: unknown): { bytes: number; json: string } {
  const json = JSON.stringify(data);
  return {
    json,
    bytes: json?.length || 0,
  };
}

/**
 * Format byte size as human-readable string.
 *
 * @param bytes - Size in bytes
 * @returns Formatted string (e.g., "50.5kb")
 */
export function formatSize(bytes: number): string {
  return `${(bytes / 1024).toFixed(1)}kb`;
}

/**
 * Extract field names from the first element of an array.
 * Returns empty array if data is not an array or first element is not an object.
 *
 * @param data - Array data to inspect
 * @returns Array of field names
 */
export function getArrayFields(data: unknown): string[] {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  const firstItem = data[0];
  if (typeof firstItem !== 'object' || firstItem === null) {
    return [];
  }

  return Object.keys(firstItem);
}

/**
 * Check if an array has objects with extractable fields.
 *
 * @param data - Data to check
 * @returns True if data is a non-empty array with object elements
 */
export function hasObjectElements(data: unknown): boolean {
  return Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && data[0] !== null;
}
